"use client";

import type { CategoriesResponse,Category } from "../types";
import { api } from "@/shared/api/api";
import { CATEGORY_API_PATHS } from "../constants/apiConstants";

export type ProductFilters = {
  categories?: string[];
  brands?: string[];
  min_price?: number;
  max_price?: number;
  min_rating?: number;
  in_stock?: boolean;
  sort?: string;
  min_discount?: number;
  page?: number;
};

export async function getCategories(): Promise<CategoriesResponse> {
  const categories = await api.get<Category[]>(CATEGORY_API_PATHS.categories);
  return { categories: categories ?? [] };
}

export async function getBrands<T = unknown>(
  categorySlug?: string,
  options?: { limit?: number },
) {
  const params = new URLSearchParams();
  if (categorySlug) params.set("category", categorySlug);
  if (options?.limit) params.set("limit", String(options.limit));
  const query = params.toString();
  const path = query ? `${CATEGORY_API_PATHS.brands}?${query}` : CATEGORY_API_PATHS.brands;
  return (await api.get<T[]>(path)) ?? [];
}

export async function getAllProducts<T = unknown, M = unknown>(
  limit: number,
  offset: number,
  filters?: ProductFilters,
) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));

  const effectivePage =
    filters?.page && Number.isFinite(filters.page) && filters.page > 0
      ? filters.page
      : Math.floor(offset / limit) + 1;
  const effectiveOffset =
    filters?.page && Number.isFinite(filters.page) && filters.page > 0
      ? (filters.page - 1) * limit
      : offset;

  params.set("page", String(effectivePage));
  params.set("offset", String(effectiveOffset));

  for (const category of filters?.categories ?? []) params.append("categories[]", category);
  for (const brand of filters?.brands ?? []) params.append("brands[]", brand);

  if (typeof filters?.min_price === "number" && Number.isFinite(filters.min_price)) {
    params.set("min_price", String(filters.min_price));
  }
  if (typeof filters?.max_price === "number" && Number.isFinite(filters.max_price)) {
    params.set("max_price", String(filters.max_price));
  }
  if (typeof filters?.min_rating === "number" && Number.isFinite(filters.min_rating)) {
    params.set("min_rating", String(filters.min_rating));
  }
  if (typeof filters?.min_discount === "number" && Number.isFinite(filters.min_discount)) {
    params.set("min_discount", String(filters.min_discount));
  }
  if (typeof filters?.in_stock === "boolean") {
    params.set("in_stock", filters.in_stock ? "true" : "false");
  }
  if (filters?.sort && filters.sort !== "default") {
    params.set("sort", filters.sort);
  }

  const raw = await api.get<unknown>(CATEGORY_API_PATHS.allProducts(params.toString()));

  const obj = raw as { data?: { products?: T[] }; meta?: M } | undefined;
  const data: T[] = obj?.data?.products ?? [];
  const meta: M | undefined = obj?.meta;

  return { data, meta };
}
